import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

const API = "/api";

const ProfileReadyBanner = () => {
  const { user, token } = useAuth();
  const [notification, setNotification] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user || !token) return;
    const dismissed_key = `profile_ready_dismissed_${user.id || user.sub}`;
    if (sessionStorage.getItem(dismissed_key)) return;

    const fetchNotifs = async () => {
      try {
        const res = await axios.get(`${API}/social/notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const profileNotif = (res.data.notifications || []).find(
          n => n.type === 'profile_ready' && !n.read
        );
        if (profileNotif) setNotification(profileNotif);
      } catch { }
    };
    fetchNotifs();
  }, [user, token]);

  const handleDismiss = async () => {
    setDismissed(true);
    const uid = user?.id || user?.sub;
    if (uid) sessionStorage.setItem(`profile_ready_dismissed_${uid}`, '1');
    if (notification && token) {
      try {
        await axios.post(`${API}/social/notifications/${notification.id}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch { }
    }
  };

  if (!notification || dismissed) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="mb-4 relative overflow-hidden rounded-xl border border-purple-500/30 bg-gradient-to-r from-purple-900/30 via-card to-indigo-900/30"
        data-testid="profile-ready-banner"
      >
        <div className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0">
            <Sparkles size={20} className="text-purple-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-purple-300">{notification.title}</p>
            <p className="text-xs text-gray-400 mt-0.5">{notification.message}</p>
          </div>
          <Link
            to="/profile/edit"
            className="shrink-0 px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-xs font-semibold transition"
            onClick={handleDismiss}
            data-testid="profile-ready-cta"
          >
            Customize
          </Link>
          <button
            onClick={handleDismiss}
            className="shrink-0 p-1 text-gray-500 hover:text-white transition"
            data-testid="profile-ready-dismiss"
          >
            <X size={16} />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ProfileReadyBanner;
